import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const [state, setState] = useState('verifying'); // verifying | success | error
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setErr('Missing verification token.'); return; }
    (async () => {
      try {
        await api.post('/auth/verify-email', { token });
        setState('success');
      } catch (e) {
        setErr(e.response?.data?.detail || 'This link is invalid or has expired.');
        setState('error');
      }
    })();
  }, [token]);

  return (
    <div className="app-container min-h-screen bg-[#F5F5F0] flex items-center justify-center px-6" data-testid="verify-email-page">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-black/[0.06] p-7 text-center">
        {state === 'verifying' && (
          <>
            <Loader2 size={28} className="animate-spin text-[#3F5200] mx-auto mb-3" />
            <p className="text-sm font-bold text-[#111111]">Verifying your email…</p>
          </>
        )}
        {state === 'success' && (
          <div data-testid="verify-success">
            <CheckCircle2 size={36} className="text-[#3F5200] mx-auto mb-3" />
            <p className="text-lg font-extrabold text-[#111111]">You&apos;re verified</p>
            <p className="text-sm text-[#6B6E73] mt-1.5 leading-relaxed">
              Welcome to ASAP — you can now host plans, post vibes and message anyone.
            </p>
            <button
              data-testid="verify-continue"
              onClick={() => navigate('/')}
              className="mt-5 w-full bg-[#B6E600] text-[#111111] font-bold rounded-full py-3 text-sm active:scale-[0.97]"
            >
              Continue to ASAP
            </button>
          </div>
        )}
        {state === 'error' && (
          <div data-testid="verify-error">
            <XCircle size={36} className="text-[#FF3B30] mx-auto mb-3" />
            <p className="text-lg font-extrabold text-[#111111]">Link expired or invalid</p>
            <p className="text-sm text-[#6B6E73] mt-1.5 leading-relaxed">{err}</p>
            <p className="text-xs text-[#9B9EA3] mt-3 leading-relaxed">
              Verification links expire after 24 hours. Tap below to send a fresh one to your inbox.
            </p>
            <button
              data-testid="verify-resend-fresh"
              onClick={async (e) => {
                e.currentTarget.disabled = true;
                try {
                  await api.post('/auth/resend-verification');
                  e.currentTarget.innerText = 'Sent — check your inbox';
                } catch (e2) {
                  e.currentTarget.innerText = e2.response?.data?.detail || 'Try again later';
                }
              }}
              className="mt-4 w-full bg-[#B6E600] text-[#111111] font-bold rounded-full py-3 text-sm active:scale-[0.97] disabled:opacity-60"
            >
              Send me a new link
            </button>
            <button
              data-testid="verify-back-home"
              onClick={() => navigate('/')}
              className="mt-2 w-full bg-[#F1F0EA] text-[#111111] font-bold rounded-full py-3 text-sm active:scale-[0.97]"
            >
              Back to home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
